import { RefObject, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

import { getImageSize } from "react-image-size";
import {
  BIAS_LAT_LON_RATIO,
  DEFAULT_MAP_LOCATION,
} from "../../../../../src/domains/shared/constants/constants";
import { COLOR_CONSTANTS } from "../../constants/styles";
import {
  IMAGE_FADE_IN_MS,
  ImageLoadingPlaceholder,
} from "../ImageLoadingPlaceholder";
import {
  ImageOverlayLoadState,
  OlMap,
  fromLonLat,
  Map,
} from "../../../../../src/domains/shared/react-openlayers";

/**
 * 줌/패닝 이미지 뷰어 props — 내부적으로 react-openlayers `Map` + `ImageOverlay` 사용.
 *
 * 이미지 비율을 지리 좌표로 변환하여 줌/패닝 구현. 전체 화면 컨테이너 채우기는 `fillContainer` 토글.
 */
export interface MapImageViewerProps {
  imageUrl: string;
  /**
   * 본 그림이 얹히기 전까지 대신 보여줄 같은 사진의 저해상본(썸네일). 이미 받아 둔 것이 있으면
   * 넘긴다 — 흐리게 상자를 채운 채 기다리다 본 그림이 오면 선명해진다.
   */
  previewImageUrl?: string;
  width?: string;
  height?: string;
  /**
   * 높이를 어디서 정하는가. `true` 면 `height` 를 그대로 써 부모를 채우고,
   * `false`(기본) 면 `height` 를 쓰지 않고 `BIAS_LAT_LON_RATIO` 비율로 높이를 잡는다 —
   * 즉 `height` 는 이 값이 `true` 일 때만 쓰인다.
   */
  fillContainer?: boolean;
}

/** 저해상본과 본 그림의 겹침 순서 — 본 그림이 위에 얹혀 같은 자리를 덮는다 */
const OVERLAY_Z = { preview: 0, main: 1 } as const;

/**
 * 본 그림이 오는 중일 때 지도 캔버스 전체에 거는 흐림 — 저해상본이 있으면 그것이 흐렸다가
 * 선명해지고, 저해상본을 넘기지 않는 화면에서도 걸리지만 그동안은 로딩 표시가 덮고 있다.
 */
const LOADING_BLUR_PX = 6;

/**
 * 저해상본에서 본 그림으로 넘어가는 데 걸리는 시간 — 카메라 이동·흐림 해제·저해상본 제거가 이 값을 쓴다.
 * 로딩 표시가 걷히는 시간(`IMAGE_FADE_IN_MS`)을 그대로 쓴다 — 그 값을 바꾸면 이 전환도 같이 바뀐다.
 */
const SWAP_TRANSITION_MS = IMAGE_FADE_IN_MS;

/**
 * 그림의 가로세로 비율을 실측한다. **0 은 "아직 모른다"**.
 * 저해상본과 본 그림은 비율이 다를 수 있어 각자 재야 한다 — 한쪽 비율을 다른 쪽에 씌우면
 * 그 그림이 늘어난다.
 */
function useImageRatio(imageUrl?: string) {
  const [measured, setMeasured] = useState<{
    url: string;
    ratio: number;
  } | null>(null);

  useEffect(() => {
    if (!imageUrl) return;
    getImageSize(imageUrl)
      .then(({ width, height }) => {
        setMeasured({
          url: imageUrl,
          ratio: (width / height) * BIAS_LAT_LON_RATIO,
        });
      })
      .catch((errorMessage) => {
        console.log(errorMessage);
      });
  }, [imageUrl]);

  // 주소가 바뀌면 옛 실측은 새 주소의 것이 아니다 — 따로 비울 필요가 없다
  return measured && measured.url === imageUrl ? measured.ratio : 0;
}

/** 어느 주소가 어떤 적재 상태인지 — 주소가 바뀌면 옛 값은 새 주소의 것이 아니라 자동 무효가 된다 */
type UrlLoadState = { url: string; state: ImageOverlayLoadState } | null;

/**
 * 기록된 적재 상태를 꺼내되 **모르면 `"loading"`** 으로 답한다 — 주소가 없어 오버레이가 아예
 * 걸리지 않았을 때도, 기록이 옛 주소의 것일 때도 그렇다.
 * 따라서 이 값의 `"failed"` 는 "오버레이가 실패를 보고했다"만 뜻한다. 주소를 끝내 얻지 못한 경우는
 * 실패와 구분되지 않고 `"loading"` 에 남는다.
 */
function loadStateOrLoading(
  load: UrlLoadState,
  url?: string
): ImageOverlayLoadState {
  return url && load?.url === url ? load.state : "loading";
}

/** 지도 좌표계에서 그 비율의 그림이 차지할 사각형 */
function boundsOf(ratio: number) {
  return [
    [DEFAULT_MAP_LOCATION.lng - ratio, DEFAULT_MAP_LOCATION.lat - 1],
    [DEFAULT_MAP_LOCATION.lng + ratio, DEFAULT_MAP_LOCATION.lat + 1],
  ] as [[number, number], [number, number]];
}

/**
 * 오버레이 한 장이 지도에 걸리기까지 필요한 것 전부 — 주소 하나만 주면 된다.
 * 저해상본과 본 그림은 비율이 달라 사각형을 공유할 수 없어, 이 묶음을 각자 하나씩 갖는다.
 */
function useOverlayImage(url?: string) {
  const [load, setLoad] = useState<UrlLoadState>(null);
  const ratio = useImageRatio(url);

  return {
    ratio,
    bounds: boundsOf(ratio),
    state: loadStateOrLoading(load, url),
    onLoadStateChange: (state: ImageOverlayLoadState) =>
      setLoad({ url: url ?? "", state }),
  };
}

/**
 * 지도를 그 사각형에 맞춘다.
 *
 * - 상자 크기를 알면 **가득 채운다**(`"cover"`) — 긴 쪽이 넘치더라도 여백을 남기지 않고,
 *   넘친 부분은 줌/패닝으로 볼 수 있다. `durationMs` 가 0 보다 크면 그 시간 동안 움직여 간다.
 *   저해상본과 본 그림은 비율이 달라 맞춤이 한 번 바뀌는데, 즉시 바꾸면 화면이 툭 튄다.
 * - 상자 크기를 아직 모르면 가득 채울 배율을 낼 수 없어, 여백을 남기더라도 **전부 보이게**
 *   넣어 둔다(`"contain"`). 이 자리는 크기를 알 때까지의 임시라 움직임 없이 즉시 놓인다.
 *
 * @returns 실제로 적용한 맞춤. `"contain"` 이면 여백이 남아 있고 `durationMs` 는 쓰이지 않았다 —
 * 호출자는 이것을 "맞췄다"로 치지 말고 다음 기회에 다시 맞춰야 한다.
 */
function frameToBounds(
  map: OlMap,
  bounds: [[number, number], [number, number]],
  durationMs: number
): "cover" | "contain" {
  const [min, max] = bounds.map((corner) => fromLonLat(corner));
  const extent: [number, number, number, number] = [
    min[0],
    min[1],
    max[0],
    max[1],
  ];
  const view = map.getView();
  const center: [number, number] = [
    (extent[0] + extent[2]) / 2,
    (extent[1] + extent[3]) / 2,
  ];

  const size = map.getSize();
  if (!size?.[0] || !size?.[1]) {
    view.setCenter(center);
    view.fit(extent, { padding: [0, 0, 0, 0] });
    return "contain";
  }

  const resolution = Math.min(
    (extent[2] - extent[0]) / size[0],
    (extent[3] - extent[1]) / size[1]
  );

  if (durationMs === 0) {
    view.setCenter(center);
    view.setResolution(resolution);
    return "cover";
  }
  view.animate({ center, resolution, duration: durationMs });
  return "cover";
}

/**
 * 이 뷰어의 **카메라 맞춤을 소유하고**, 저해상본을 떼기까지 기다리는 시계를 함께 돌린다 —
 * 저해상본을 넘기지 않는 화면에서도 그림을 상자에 맞추는 곳은 이 훅뿐이다.
 * 맞춤과 저해상본 제거는 같은 `SWAP_TRANSITION_MS` 를 쓰면서 시작 시점이 서로 달라, 흩어 두면
 * 어느 시점에 무엇이 보이는지 읽을 수 없다. 흐림도 같은 값을 쓰지만 그 시계는 CSS transition 이라
 * `MapImageViewer` 의 sx 가 갖는다 — 여기서 돌리지 않는다.
 */
function useImageFramingAndSwap({
  mapRef,
  framedRatio,
  imageUrl,
  mainState,
}: {
  mapRef: RefObject<OlMap | null>;
  framedRatio: number;
  imageUrl: string;
  mainState: ImageOverlayLoadState;
}) {
  /** 첫 맞춤은 보여 줄 것이 없던 상태에서 오므로 즉시, 그 뒤 비율이 바뀌는 맞춤은 움직여 간다 */
  const hasFramedRef = useRef(false);
  const [settledUrl, setSettledUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!mapRef.current || framedRatio === 0) return;
    const framed = frameToBounds(
      mapRef.current,
      boundsOf(framedRatio),
      hasFramedRef.current ? SWAP_TRANSITION_MS : 0
    );
    // 여백이 남은 임시 맞춤은 "맞췄다"로 치지 않는다 — 다음 비율이 오면 즉시 다시 가득 채운다
    hasFramedRef.current = framed === "cover";
  }, [mapRef, framedRatio]);

  useEffect(() => {
    if (mainState !== "drawn") return;
    const timer = setTimeout(() => setSettledUrl(imageUrl), SWAP_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [mainState, imageUrl]);

  return {
    /**
     * 흐림이 풀리고 `SWAP_TRANSITION_MS` 가 더 지나야 참이 된다 — 맞춤이 움직이는 동안엔
     * 본 그림이 상자를 다 못 덮는 순간이 있어, 저해상본을 먼저 떼면 그 틈이 드러난다.
     */
    isMainSettled: Boolean(imageUrl) && settledUrl === imageUrl,
  };
}

export const MapImageViewer = ({
  imageUrl,
  previewImageUrl,
  width = "100%",
  height = "100%",
  fillContainer = false,
}: MapImageViewerProps) => {
  const mapRef = useRef<OlMap>(null);
  const main = useOverlayImage(imageUrl);
  const preview = useOverlayImage(previewImageUrl);

  /** 화면을 맞출 기준 — 본 그림 비율을 알면 그것으로, 아직이면 저해상본 비율로 */
  const framedRatio = main.ratio || preview.ratio;

  const { isMainSettled } = useImageFramingAndSwap({
    mapRef,
    framedRatio,
    imageUrl,
    mainState: main.state,
  });

  const hasImage = main.state === "drawn" || preview.state === "drawn";

  /**
   * 흐림은 본 그림이 아직 오는 중일 때만 걸린다 — 그려지는 순간은 물론 **오버레이가 적재
   * 실패를 보고했을 때**도 풀린다. 저해상본이 최종 결과인데 흐린 채 두면 사진이 망가진 것처럼
   * 보인다.
   * 본 그림 **주소 자체를 끝내 얻지 못한 경우**는 `loadStateOrLoading` 이 `"loading"` 으로
   * 접어 실패와 구분되지 않으므로 흐림이 남는다.
   */
  const isBlurred = main.state === "loading";

  /** 저해상본을 걸어 둘 주소 — 비율이 잡힌 뒤부터 전환이 끝날 때까지 있다 */
  const previewOverlayUrl =
    previewImageUrl && preview.ratio > 0 && !isMainSettled
      ? previewImageUrl
      : null;

  return (
    <Box
      sx={{
        width,
        ...(fillContainer ? { height } : { aspectRatio: BIAS_LAT_LON_RATIO }),
        position: "relative",
        overflow: "hidden",
        background: COLOR_CONSTANTS._components.backdrop.fill,
        "& canvas": {
          filter: isBlurred ? `blur(${LOADING_BLUR_PX}px)` : "none",
          transition: `filter ${SWAP_TRANSITION_MS}ms ease`,
        },
      }}
    >
      <Map
        hasScaleLine={false}
        ref={mapRef}
        isShownOsm={false}
        zoomLevel={7}
        center={[DEFAULT_MAP_LOCATION.lng, DEFAULT_MAP_LOCATION.lat]}
        width="100%"
        height="100%"
      >
        {/* 전환이 끝난 뒤에야 저해상본을 뗀다 — 그때는 본 그림이 상자를 다 덮고 있다 */}
        {previewOverlayUrl && (
          <Map.ImageOverlay
            key={`preview-${previewOverlayUrl}`}
            imageUrl={previewOverlayUrl}
            bounds={preview.bounds}
            zIndex={OVERLAY_Z.preview}
            onLoadStateChange={preview.onLoadStateChange}
          />
        )}
        {imageUrl && main.ratio > 0 && (
          <Map.ImageOverlay
            key={imageUrl}
            imageUrl={imageUrl}
            bounds={main.bounds}
            zIndex={OVERLAY_Z.main}
            onLoadStateChange={main.onLoadStateChange}
          />
        )}
      </Map>
      {/* 아직 아무 그림도 얹히지 않은 동안만 덮는다 — 걷힐 때 그림이 서서히 드러난다 */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: hasImage ? 0 : 1,
          // 투명해지기만 하면 보조 기술에는 "불러오는 중"이 계속 남는다 — 다 걷힌 뒤 감춘다
          visibility: hasImage ? "hidden" : "visible",
          transition: `opacity ${IMAGE_FADE_IN_MS}ms ease, visibility 0s linear ${
            hasImage ? IMAGE_FADE_IN_MS : 0
          }ms`,
          pointerEvents: "none",
        }}
      >
        <ImageLoadingPlaceholder size="large" />
      </Box>
    </Box>
  );
};
